import React, { useState } from 'react';
import { CaretDown, CaretUp, CircleNotch } from '@phosphor-icons/react';
import useAnnouncer from '@/hooks/useAnnouncer';
import { DynamicSectionRenderer } from '../DynamicSectionRenderer';
import { GenericTaskDetail } from './GenericTaskDetail';
import { ContractText, ContractBadge } from '@/components/system/ContractUI';
import { AGENT_CONFIG, INTENT_TO_BUTTON } from '@/config/task-display-config';

/**
 * EmailTaskDetail - Contract-Driven Email Task Renderer
 * Authority: Law 65.1 (UI Technical Field Prohibition) + Law 66.1 (Dynamic Labels)
 * 
 * Renders email tasks using contract configuration - NO hard-coded labels
 */

// Hardcoded fallback configuration (only used if backend contract missing)
const FALLBACK_CONFIG = {
    sections: [
        { field: "preview", label: null, render_type: "email_preview", required: true },
        { field: "intent", label: "Intent", render_type: "text", required: false }
    ]
};

export function EmailTaskDetail({ item, uiConfig, onAccept, onReject }) {
    // --- PHASE 2: UI DETAIL CONTRACT ENFORCEMENT ---
    // Authority: item.payload.preview

    const [recipientsOpen, setRecipientsOpen] = useState(false);
    // WI-INBOX-BUTTONS-001: local busy state so the two action buttons can
    // mutually disable while one is in flight. The parent hook
    // (useInboxCockpitState) handles the actual promise + toast + refresh.
    const [busyAction, setBusyAction] = useState(null); // 'approve' | 'reject' | null

    // 1. Extract Contract Data
    const preview = item.payload?.preview;
    const hasValidPreview = preview && preview.html_body && preview.subject;
    const recipientsCount = item.payload?.recipients_count || 0;
    const recipientsList = item.payload?.recipients_list || [];
    const intent = item.payload?.intent || 'Standard';

    // WI-GATEENVELOPE-SILENT-200-AUDIT-AND-FIX-001 (L-055): the accept/reject
    // endpoints can now return a real non-200. A toast is not reliably announced
    // (#950), so the failure is spoken here — otherwise the button just stops
    // spinning and the CEO is told nothing at all.
    const [actionStatus, announceAction] = useAnnouncer();

    const handleApprove = async () => {
        if (!onAccept || busyAction) return;
        setBusyAction('approve');
        try {
            await onAccept(item.id, item.work_type, {});
        } catch (err) {
            announceAction(err?.message || 'Could not approve this item — please try again.');
        } finally {
            setBusyAction(null);
        }
    };

    const handleReject = async () => {
        if (!onReject || busyAction) return;
        setBusyAction('reject');
        try {
            await onReject(item.id, 'rejected', item.work_type);
        } catch (err) {
            announceAction(err?.message || 'Could not reject this item — please try again.');
        } finally {
            setBusyAction(null);
        }
    };

    // 2. Render Enforced View
    // WI-INBOX-BUTTONS-001: action bar positioned just below the header
    // metadata (not sticky-bottom). Rationale: position:sticky inside a
    // nested flex column with dynamic height was unreliable — the buttons
    // rendered at y=1772 (bottom of content) instead of pinning to the
    // viewport. Top placement keeps the buttons visible immediately on
    // card open without requiring the user to scroll through a long email.
    return (
        <div className="space-y-6">
            <div role="status" aria-live="polite" className="sr-only">{actionStatus}</div>
            {/* Header Metadata */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100">
                <div>
                    <ContractText variant="sectionLabel">Campaign Configuration</ContractText>
                    <ContractText variant="caption" className="text-slate-500 mt-1">
                        Intent: <span className="font-mono text-slate-700">{intent}</span>
                    </ContractText>
                </div>
                <div className="text-right">
                    <ContractText variant="stat">{recipientsCount}</ContractText>
                    <ContractText variant="caption" className="tracking-wide">Recipients</ContractText>
                </div>
            </div>

            {/* Action Bar — WI-INBOX-BUTTONS-001.
                Reject (left, negative) + Approve & Send (right, primary).
                44px min-height; INTENT_TO_BUTTON tokens; mutual-disable
                while either action is in flight. Positioned high in the
                card so the buttons are visible without scrolling through
                a long email body. */}
            <div
                data-testid="email-task-action-bar"
                className="flex items-center py-3 px-4 rounded-lg border border-slate-200 bg-white"
            >
                <button
                    type="button"
                    data-testid="email-reject-btn"
                    onClick={handleReject}
                    disabled={!!busyAction || !onReject}
                    className={`min-h-[44px] px-5 py-2.5 rounded-md font-semibold text-sm transition-colors border ${INTENT_TO_BUTTON.negative.bg} ${INTENT_TO_BUTTON.negative.text} ${INTENT_TO_BUTTON.negative.border} ${INTENT_TO_BUTTON.negative.hover} disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2`}
                    style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
                >
                    {busyAction === 'reject' && (
                        <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
                    )}
                    {busyAction === 'reject' ? 'Rejecting...' : 'Reject'}
                </button>

                <div className="flex-1" />

                <button
                    type="button"
                    data-testid="email-approve-btn"
                    onClick={handleApprove}
                    disabled={!!busyAction || !onAccept}
                    className={`min-h-[44px] px-5 py-2.5 rounded-md font-semibold text-sm transition-colors ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2`}
                    style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
                >
                    {busyAction === 'approve' && (
                        <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
                    )}
                    {busyAction === 'approve' ? 'Sending...' : 'Approve & Send'}
                </button>
            </div>

            {/* Recipients List — Expandable (WI4/RL1-RL2) */}
            {recipientsList.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                        onClick={() => setRecipientsOpen(!recipientsOpen)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors min-h-[44px] cursor-pointer"
                        aria-expanded={recipientsOpen}
                    >
                        <ContractText variant="sectionLabel">
                            {recipientsCount} {recipientsCount === 1 ? 'Recipient' : 'Recipients'}
                        </ContractText>
                        {recipientsOpen
                            ? <CaretUp weight="bold" className="w-4 h-4 text-slate-500" />
                            : <CaretDown weight="bold" className="w-4 h-4 text-slate-500" />
                        }
                    </button>
                    {recipientsOpen && (
                        <div className="divide-y divide-slate-100">
                            {recipientsList.map((r, i) => (
                                <div key={i} className="px-4 py-3 text-base text-slate-800">
                                    <span className="font-semibold">{r.recipient_name}</span>
                                    {r.company_name && (
                                        <span className="text-slate-500"> — {r.company_name}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Preview Section */}
            <div>
                <div className="mb-3 flex items-center gap-2">
                    <ContractText variant="sectionLabel">Message Preview</ContractText>

                    {hasValidPreview ? (
                        <ContractBadge intent="success">Ready</ContractBadge>
                    ) : (
                        <ContractBadge intent="error">Missing</ContractBadge>
                    )}
                </div>

                {hasValidPreview ? (
                    <div className="border border-brand-100 rounded-lg overflow-hidden shadow-sm">
                        <div className="bg-brand-50/50 px-4 py-3 border-b border-brand-100">
                            <div className="text-sm font-medium text-slate-900">
                                <ContractText variant="caption" as="span" className="font-normal mr-2">Subject:</ContractText>
                                {preview.subject}
                            </div>
                        </div>
                        <div className="p-6 bg-white min-h-[300px]">
                            <div
                                className="prose prose-sm max-w-none text-slate-800 font-sans"
                                dangerouslySetInnerHTML={{ __html: preview.html_body }}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="p-8 border-2 border-dashed border-slate-200 rounded-lg text-center text-slate-500 bg-slate-50">
                        <p>No preview content available.</p>
                        <ContractText variant="caption" className="mt-1 text-slate-400">The contracts returned an empty or invalid preview object.</ContractText>
                    </div>
                )}
            </div>

            {/* Debug / Payload Dump (Dev Mode) */}
            {process.env.NODE_ENV === 'development' && (
                <details className="text-xs text-slate-400 cursor-pointer">
                    <summary>Raw Payload Contract</summary>
                    <pre className="mt-2 p-2 bg-slate-100 rounded overflow-x-auto">
                        {JSON.stringify(item.payload, null, 2)}
                    </pre>
                </details>
            )}
        </div>
    );
}
